// Annuaire des clubs (D-094) : logique pure, sans réseau ni écran (testée dans annuaire.test.ts).
// Pendant la synchro FFBB d'une équipe, l'appareil du staff relève les organismes adverses de NOS
// rencontres (jamais le reste de la poule), lit leurs fiches par paquets, réduit leurs logos, et confie
// le tout à la base (club_directory_link puis club_directory_save), qui revérifie et borne chaque champ.
import { isEmail } from './derogation';

/** Fiches lues en une requête FFBB (filtre id _in). */
export const READ_BATCH = 40;
/** Logo réduit : côté maximal en pixels, et longueur maximale de son adresse data: (la base refuse au-delà). */
export const LOGO_SIDE = 128;
export const LOGO_MAX = 24000;
/** Champs de la fiche d'un organisme (items/ffbbserver_organismes), relevés le 2026-10-28. */
export const ORG_FIELDS = ['id', 'code', 'nom', 'mail', 'telephone', 'urlSiteWeb', 'logo', 'commune.libelle',
  'organisme_id_pere.nom', 'organisme_id_pere.organisme_id_pere.nom'];

type Ref = string | number | { id?: string | number | null; idOrganisme?: { id?: string | number | null } | null } | null | undefined;

/** L'engagement d'une rencontre : l'identifiant seul, ou l'objet quand la FFBB développe le champ
 *  (idEngagementEquipe1.id, idEngagementEquipe1.idOrganisme.id). */
export function engagementOf(v: Ref): string {
  if (v && typeof v === 'object') return v.id != null ? String(v.id) : '';
  return v == null ? '' : String(v);
}

/** L'organisme (le club) d'un engagement développé ; null s'il manque. */
export function organismeOf(v: Ref): string | null {
  const id = v && typeof v === 'object' ? v.idOrganisme?.id : null;
  return id != null && /^[0-9]{1,20}$/.test(String(id)) ? String(id) : null;
}

export interface OpponentPair { ffbb_id: string; org_id: string }

/** L'organisme adverse de chacune de NOS rencontres (celles de notre engagement) ; les autres
 *  rencontres de la poule sont ignorées. */
export function opponentPairs(rencontres: { id: string | number; idEngagementEquipe1: Ref; idEngagementEquipe2: Ref }[], ourEngagement: string): OpponentPair[] {
  const out: OpponentPair[] = [];
  for (const r of rencontres) {
    const e1 = engagementOf(r.idEngagementEquipe1), e2 = engagementOf(r.idEngagementEquipe2);
    const other = e1 === ourEngagement ? r.idEngagementEquipe2 : e2 === ourEngagement ? r.idEngagementEquipe1 : null;
    const org = organismeOf(other);
    if (org) out.push({ ffbb_id: String(r.id), org_id: org });
  }
  return out;
}

/** La fiche développée de l'organisme adverse d'une de nos rencontres (lecture de repli de la pré-demande
 *  de dérogation) ; null si la rencontre n'est pas à nous. */
export function opponentOrgObject(row: { idEngagementEquipe1?: unknown; idEngagementEquipe2?: unknown } | null | undefined, ourEngagement: string | null): Record<string, any> | null {
  if (!row || !ourEngagement) return null;
  const e1 = row.idEngagementEquipe1 as Ref, e2 = row.idEngagementEquipe2 as Ref;
  const other = engagementOf(e1) === ourEngagement ? e2 : engagementOf(e2) === ourEngagement ? e1 : null;
  const org = other && typeof other === 'object' ? (other as { idOrganisme?: unknown }).idOrganisme : null;
  return org && typeof org === 'object' ? org as Record<string, any> : null;
}

/** Une liste en paquets de n. */
export const batches = <T>(list: T[], n = READ_BATCH): T[][] =>
  Array.from({ length: Math.ceil(list.length / n) }, (_, i) => list.slice(i * n, i * n + n));

// --- Mise en forme (mêmes règles que scripts/prospects/ffbb-import.mjs) ------------------------------------
const SMALL = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'et', 'en', 'sur', 'sous', 'à', 'au', 'aux', 'd', 'l']);
const ACRO = new Set(['ab', 'abc', 'al', 'als', 'as', 'asc', 'asl', 'aso', 'asptt', 'bc', 'bca', 'bcm', 'ca', 'cb', 'cbo', 'cep', 'cs', 'eb', 'ebc', 'es', 'esb',
  'esc', 'fc', 'fjep', 'gs', 'jaf', 'js', 'jsa', 'jsc', 'ob', 'rc', 'sa', 'sb', 'sc', 'sco', 'sl', 'so', 'st', 'ua', 'ub', 'uba', 'us', 'usa', 'usc', 'uso', 'cto', 'ctc']);

/** « ISLE-JOURDAIN » → « Isle-Jourdain », « BC DES TROIS-PONTS » → « BC des Trois-Ponts ». */
export function titleCase(s: string): string {
  return s.trim().toLowerCase().split(/\s+/).map((w, i) => {
    if (ACRO.has(w) || (/^\p{L}{2,4}$/u.test(w) && !/[aeiouyàâäéèêëîïôöûü]/.test(w))) return w.toUpperCase();
    if (i > 0 && SMALL.has(w)) return w;
    if (i > 0 && /^[dl]['’]/.test(w)) return w.slice(0, 2) + w.charAt(2).toUpperCase() + w.slice(3);
    return w.replace(/(^|[-'’])(\p{L})/gu, (_, sep: string, c: string) => sep + c.toUpperCase());
  }).join(' ');
}
/** Un texte tout en capitales (FFBB) est remis en forme ; une casse mixte reste telle quelle. */
export const caps = (s: string | null | undefined): string => {
  const t = String(s ?? '').trim();
  return t && t === t.toUpperCase() ? titleCase(t) : t;
};

/** « COMITE DEPARTEMENTAL DE BASKET-BALL DU GERS » → « Comité du Gers ». */
export function committeeName(s: string | null | undefined): string | null {
  if (!s) return null;
  const t = titleCase(String(s).replace(/D[ÉE]PARTEMENTAL[E]?\s*/i, '').replace(/(DE\s+)?BASKET[- ]?BALL\s*/i, '').replace(/\s+/g, ' '))
    .replace(/^Comite\b/, 'Comité');
  return t ? t.slice(0, 120) : null;
}

const REGIONS: Record<string, string> = {
  ARA: 'Auvergne-Rhône-Alpes', BFC: 'Bourgogne-Franche-Comté', BRE: 'Bretagne', CVL: 'Centre-Val de Loire', COR: 'Corse', GES: 'Grand Est',
  HDF: 'Hauts-de-France', IDF: 'Île-de-France', NOR: 'Normandie', NAQ: 'Nouvelle-Aquitaine', OCC: 'Occitanie', PDL: 'Pays de la Loire',
  PCA: 'Provence-Alpes-Côte d’Azur', PAC: 'Provence-Alpes-Côte d’Azur',
};
/** La région : d'après les 3 lettres du code (ligue), sinon le nom de la ligue lu sur la FFBB. */
export function regionOf(code: string, ligue?: string | null): string | null {
  const known = REGIONS[code.slice(0, 3)];
  if (known) return known;
  const t = titleCase(String(ligue ?? '').replace(/LIGUE( R[ÉE]GIONALE)?\s*(D['’]|DE |DU |DES )?/i, '').replace(/\s*DE BASKET.*$/i, ''));
  return t ? t.slice(0, 60) : null;
}

/** Le téléphone en chiffres (« 06.50.30.00.56 » → « 0650300056 ») ; null s'il n'en est pas un. */
export function phoneDigits(s: unknown): string | null {
  const d = String(s ?? '').replace(/[^0-9+]/g, '');
  return /^\+?[0-9]{6,15}$/.test(d) ? d : null;
}
/** « 0650300056 » → « 06 50 30 00 56 » (numéro français) ; tel quel sinon. */
export const phoneLabel = (d: string): string => (/^0[0-9]{9}$/.test(d) ? d.replace(/(\d{2})(?=\d)/g, '$1 ') : d);

/** Le site du club : une adresse http(s) sans espace ni guillemet ; « www.club.fr » devient https://. */
export function websiteOf(s: unknown): string | null {
  const t = String(s ?? '').trim();
  const url = /^https?:\/\//i.test(t) ? t : /^www\.[^\s/]+\.[a-z]{2,}/i.test(t) ? `https://${t}` : '';
  return url && url.length <= 300 && /^https?:\/\/[^\s"'<>]+$/i.test(url) ? url : null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Ce que l'annuaire garde d'un club ; « logo » absent : inchangé, null : pas de logo. */
export interface DirectoryRow {
  org_id: string; code: string | null; name: string; town: string | null; email: string | null; phone: string | null;
  website: string | null; committee: string | null; region: string | null; logo_ref: string | null; logo?: string | null;
}

/** La fiche FFBB d'un organisme, mise en forme pour l'annuaire ; null si elle est inutilisable. */
export function directoryRow(o: Record<string, any> | null | undefined): DirectoryRow | null {
  const id = String(o?.id ?? '');
  const name = String(o?.nom ?? '').replace(/\s+/g, ' ').trim();
  if (!o || !/^[0-9]{1,20}$/.test(id) || !name) return null;
  const code = String(o.code ?? '').trim().toUpperCase();
  const okCode = /^[A-Z]{3}[0-9]{7}$/.test(code);
  const mail = String(o.mail ?? '').trim().toLowerCase();
  const logo = String(o.logo ?? '').trim().toLowerCase();
  return {
    org_id: id, code: okCode ? code : null, name: name.slice(0, 160),
    town: o.commune?.libelle ? caps(o.commune.libelle).slice(0, 120) || null : null,
    email: mail.length <= 200 && isEmail(mail) ? mail : null,
    phone: phoneDigits(o.telephone), website: websiteOf(o.urlSiteWeb),
    committee: committeeName(o.organisme_id_pere?.nom), region: okCode ? regionOf(code, o.organisme_id_pere?.organisme_id_pere?.nom) : null,
    logo_ref: UUID.test(logo) ? logo : null,
  };
}

/** Ce que la base demande de (re)lire : l'organisme, et le logo qu'elle garde déjà. */
export interface DueOrg { org_id: string; logo_ref: string | null; has_logo: boolean }

/** Faut-il télécharger le logo ? Oui si la FFBB en publie un et que ce n'est pas celui déjà gardé. */
export const logoToFetch = (row: DirectoryRow, due?: DueOrg | null): boolean =>
  !!row.logo_ref && !(due?.has_logo && due.logo_ref === row.logo_ref);

/** La fiche à envoyer : sans logo publié, « plus de logo » (null) ; logo déjà gardé ou téléchargement raté,
 *  pas de champ « logo » (la base garde le sien) ; sinon le logo réduit, s'il est acceptable. */
export function withLogo(row: DirectoryRow, due: DueOrg | null | undefined, fetched: string | null | undefined): DirectoryRow {
  const { logo: _drop, ...rest } = row;
  if (!row.logo_ref) return { ...rest, logo: null };
  if (!logoToFetch(row, due) || !logoAccepted(fetched)) return rest;
  return { ...rest, logo: fetched };
}

/** Les dimensions du logo réduit : dans un carré de `side`, proportions gardées, jamais agrandi. */
export function fitBox(w: number, h: number, side = LOGO_SIDE): { w: number; h: number } {
  if (!(w > 0) || !(h > 0)) return { w: side, h: side };
  const k = Math.min(1, side / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/** Le cadre utile d'un logo : les pixels ni transparents ni presque blancs (les marges blanches des images
 *  FFBB sont retirées, le logo remplit son rond), plus une petite marge ; toute l'image s'il n'y a rien.
 *  `px` : les pixels RGBA d'un canvas (getImageData). */
export function trimBox(px: ArrayLike<number>, w: number, h: number, margin = 0.04): { x: number; y: number; w: number; h: number } {
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (px[i + 3]! < 24 || (px[i]! > 240 && px[i + 1]! > 240 && px[i + 2]! > 240)) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return { x: 0, y: 0, w, h };
  const m = Math.round(Math.max(x1 - x0 + 1, y1 - y0 + 1) * margin);
  x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m); x1 = Math.min(w - 1, x1 + m); y1 = Math.min(h - 1, y1 + m);
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Un logo que la base acceptera : image WebP, JPEG ou PNG en base64, sous la limite. */
export const logoAccepted = (url: string | null | undefined): url is string =>
  !!url && url.length <= LOGO_MAX && /^data:image\/(webp|jpeg|png);base64,[A-Za-z0-9+/]+=*$/.test(url);

/** L'adresse du logo réduit par la FFBB elle-même (Directus : width, height, fit, format) : 256 px, quelques Ko
 *  au lieu de ~50 ; l'appareil retire les marges et le ramène à 128 px. */
export function logoAssetUrl(base: string, ref: string, token: string, reduced = true): string {
  const u = new URL(`assets/${ref}`, base);
  if (reduced) {
    u.searchParams.set('width', String(LOGO_SIDE * 2)); u.searchParams.set('height', String(LOGO_SIDE * 2));
    u.searchParams.set('fit', 'inside'); u.searchParams.set('format', 'webp'); u.searchParams.set('quality', '80');
  }
  u.searchParams.set('access_token', token);
  return u.toString();
}
